import { mediaQueryLarge, isMobileBreakpoint } from '@theme/utilities';

/*
 * REVER — Z1: inside the footer, "mobile" is decided on the width of the PAGE,
 * not of the viewport.
 *
 * From 990px of viewport the cart drawer opens beside the page and squeezes it
 * by 30rem (.page-wrapper--drawer-open in assets/base.css): the viewport keeps
 * its width, the page does not. The footer CSS in assets/rever-custom.css
 * follows the page width with @container on <footer>; this file keeps the
 * accordion behaviour (open state and click handling) in step with it, so the
 * footer menus become accordions whenever the page is narrower than 750px,
 * cart open or not.
 *
 * Every other accordion (product page, filters, header) keeps the theme's
 * viewport logic. To move another area to the page width, convert its CSS to
 * @container first and then add its container to PAGE_WIDTH_CONTAINERS.
 *
 * REVER — Z1 (Raden, 28/10/2026, footer fix n. 6): on mobile the footer menus
 * (MENU, SOCIAL, LINK UTILI) work like the FAQ accordion
 * (sections/rever-faq.liquid): opening one closes the one that was open.
 * See #handleToggle. Every other accordion is left as the theme makes it.
 */
const PAGE_WIDTH_CONTAINERS = 'footer';
const PAGE_DESKTOP_MIN_WIDTH = 750;

/*
 * REVER — Z1: how long the menu closed by #handleToggle keeps
 * [data-rever-closing]. It must outlast the theme's closing animation
 * (--animation-speed-slow, 0.2s on 28/10/2026): the duration is read from that
 * variable and doubled, with this value as the fallback when it can't be read.
 */
const CLOSING_FALLBACK_MS = 400;

// Accordion
// Still extends HTMLElement over Component so that refs are still available to parent components (e.g. SortingFilterComponent)
class AccordionCustom extends HTMLElement {
  /** @type {HTMLDetailsElement} */
  get details() {
    const details = this.querySelector('details');

    if (!(details instanceof HTMLDetailsElement)) throw new Error('Details element not found');

    return details;
  }

  /** @type {HTMLElement} */
  get summary() {
    const summary = this.details.querySelector('summary');

    if (!(summary instanceof HTMLElement)) throw new Error('Summary element not found');

    return summary;
  }

  get #disableOnMobile() {
    return this.dataset.disableOnMobile === 'true';
  }

  get #disableOnDesktop() {
    return this.dataset.disableOnDesktop === 'true';
  }

  get #closeWithEscape() {
    return this.dataset.closeWithEscape === 'true';
  }

  #controller = new AbortController();

  /** REVER — Z1: watches the page-width container, when this accordion has one. */
  /** @type {ResizeObserver | null} */
  #pageObserver = null;

  /** REVER — Z1: last mobile/desktop state seen by the observer. */
  /** @type {boolean | null} */
  #wasMobile = null;

  /**
   * REVER — Z1: the element whose width decides mobile/desktop for this
   * accordion, or null when the viewport decides (theme default).
   *
   * @returns {Element | null}
   */
  get #pageContainer() {
    return this.closest(PAGE_WIDTH_CONTAINERS);
  }

  /**
   * REVER — Z1: mobile/desktop for this accordion. Same threshold as the
   * `@container rever-page (max-width: 749px)` rules in rever-custom.css.
   *
   * @returns {boolean}
   */
  #isMobile() {
    const container = this.#pageContainer;

    if (!container) return isMobileBreakpoint();

    return container.getBoundingClientRect().width < PAGE_DESKTOP_MIN_WIDTH;
  }

  connectedCallback() {
    const { signal } = this.#controller;

    this.#setDefaultOpenState();

    this.addEventListener('keydown', this.#handleKeyDown, { signal });
    this.summary.addEventListener('click', this.handleClick, { signal });
    this.details.addEventListener('click', this.#handleContentClick, { signal });
    // REVER — Z1: one footer menu open at a time on mobile (see #handleToggle).
    this.details.addEventListener('toggle', this.#handleToggle, { signal });

    const container = this.#pageContainer;

    if (container && 'ResizeObserver' in window) {
      // REVER — Z1: the page changes width without the viewport changing
      // (cart drawer opening or closing beside it), so a media query cannot
      // see it. Reset the open state only when mobile/desktop actually flips,
      // otherwise every resize would close a menu the user has just opened.
      this.#wasMobile = this.#isMobile();
      this.#pageObserver = new ResizeObserver(() => {
        const isMobile = this.#isMobile();

        if (isMobile === this.#wasMobile) return;

        this.#wasMobile = isMobile;
        this.#setDefaultOpenState();
      });
      this.#pageObserver.observe(container);
    } else {
      mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange, { signal });
    }
  }

  /**
   * Handles the disconnect event.
   */
  disconnectedCallback() {
    // Disconnect all the event listeners
    this.#controller.abort();
    this.#pageObserver?.disconnect();
    this.#pageObserver = null;
  }

  /**
   * Handles the click event.
   * @param {Event} event - The event.
   */
  handleClick = (event) => {
    const isMobile = this.#isMobile();
    const isDesktop = !isMobile;

    // Stop default behaviour from the browser
    if ((isMobile && this.#disableOnMobile) || (isDesktop && this.#disableOnDesktop)) {
      event.preventDefault();
      return;
    }
  };

  /**
   * Closes the accordion when a click lands inside an element marked with
   * `[data-accordion-close]` (or one of its descendants). Used by markup that
   * provides an explicit close-target region inside the content area.
   *
   * @param {MouseEvent} event - The click event.
   */
  #handleContentClick = (event) => {
    if (!this.details.open) return;

    if (!(/** @type {Element} */ (event.target).closest('[data-accordion-close]'))) return;

    this.summary.focus({ preventScroll: true });
    this.details.open = false;
  };

  /**
   * REVER — Z1: one footer menu open at a time, like the FAQ accordion.
   *
   * Runs when this accordion's <details> opens. Acts only when:
   * - the accordion is inside the page-width container (the footer);
   * - the page is in its mobile layout (narrower than 750px), the only one
   *   where the footer menus are accordions: on desktop they are always open
   *   and nothing changes;
   * - the accordion is a real accordion on mobile (not data-disable-on-mobile,
   *   i.e. a menu with "Show as accordion" off, which stays open).
   * Then it closes every other open accordion of the same footer.
   *
   * The theme animates an accordion only while it holds the focus
   * (snippets/accordion-custom-component.liquid, :not(:focus-within) means
   * transition: none). The menu closed here does not hold it and would snap
   * shut, making the menu just opened jump up. For the length of the
   * animation it gets [data-rever-closing], and assets/rever-custom.css
   * gives it back the theme's transition ("Footer mobile — un menu aperto
   * alla volta").
   *
   * The toggle event also fires when #setDefaultOpenState opens the menus on
   * desktop: there #isMobile() is false and nothing happens.
   */
  #handleToggle = () => {
    if (!this.details.open) return;

    const container = this.#pageContainer;

    if (!container || this.#disableOnMobile || !this.#isMobile()) return;

    container.querySelectorAll('accordion-custom').forEach((other) => {
      if (other === this || !(other instanceof AccordionCustom)) return;
      if (other.dataset.disableOnMobile === 'true') return;

      const details = other.querySelector('details');

      if (!(details instanceof HTMLDetailsElement) || !details.open) return;

      details.setAttribute('data-rever-closing', '');
      details.open = false;
      window.setTimeout(() => details.removeAttribute('data-rever-closing'), AccordionCustom.#closingTime(details));
    });
  };

  /**
   * REVER — Z1: how long [data-rever-closing] stays on a menu being closed:
   * twice the theme's --animation-speed-slow, or CLOSING_FALLBACK_MS.
   *
   * @param {Element} element
   * @returns {number}
   */
  static #closingTime(element) {
    const value = getComputedStyle(element).getPropertyValue('--animation-speed-slow').trim();
    const amount = parseFloat(value);

    if (!Number.isFinite(amount) || amount <= 0) return CLOSING_FALLBACK_MS;

    const milliseconds = value.endsWith('ms') ? amount : amount * 1000;

    return Math.max(milliseconds * 2, 100);
  }

  /**
   * Handles the media query change event.
   */
  #handleMediaQueryChange = () => {
    this.#setDefaultOpenState();
  };

  /**
   * Sets the default open state of the accordion based on the `open-by-default-on-mobile` and `open-by-default-on-desktop` attributes.
   */
  #setDefaultOpenState() {
    const isMobile = this.#isMobile();

    this.details.open =
      (isMobile && this.hasAttribute('open-by-default-on-mobile')) ||
      (!isMobile && this.hasAttribute('open-by-default-on-desktop'));
  }

  /**
   * Handles keydown events for the accordion
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    // Close the accordion when used as a menu
    if (event.key === 'Escape' && this.#closeWithEscape) {
      event.preventDefault();

      this.summary.focus();
      this.details.open = false;
    }
  }
}

if (!customElements.get('accordion-custom')) {
  customElements.define('accordion-custom', AccordionCustom);
}